import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Package, Printer, ArrowLeft, RefreshCw, AlertCircle, MapPin, CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LuggageManifestPage = () => {
  const { tripId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const printRef = useRef(null);

  const [trips, setTrips] = useState([]);
  const [stations, setStations] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(tripId || '');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [manifestData, setManifestData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTrips();
  }, []);

  useEffect(() => {
    if (selectedTripId) {
      fetchManifest(selectedTripId);
    }
  }, [selectedTripId, selectedDestination]);

  const fetchTrips = async () => {
    try {
      const [tripsRes, stationsRes] = await Promise.all([
        axios.get(`${API_URL}/trips`),
        axios.get(`${API_URL}/stations`)
      ]);
      setTrips(tripsRes.data || []);
      setStations(stationsRes.data || []);
      if (!selectedTripId && tripsRes.data.length > 0) {
        setSelectedTripId(tripsRes.data[0].id);
      }
    } catch (err) {
      console.error('Failed to load trips:', err);
    }
  };

  const fetchManifest = async (id) => {
    setLoading(true);
    try {
      const params = selectedDestination ? { destination: selectedDestination } : {};
      const res = await axios.get(`${API_URL}/trips/${id}/luggage-manifest`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setManifestData(res.data);
    } catch (err) {
      console.error('Failed to load luggage manifest:', err);
      toast.error('Failed to load luggage manifest');
      setManifestData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const trip = manifestData?.trip || manifestData || {};
  const luggageList = manifestData?.items || manifestData?.luggage || [];
  const parcelPoints = manifestData?.parcel_drop_pick_points || manifestData?.route_parcel_points || [];
  const routeDisplay = trip.route_name || trip.route || (
    trip.origin && trip.destination ? `${trip.origin} → ${trip.destination}` : 'Route not available'
  );
  const departureValue = trip.departure_datetime || trip.departure;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="luggage-manifest-page">
        {/* Actions bar (Hidden in Print) */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 print:hidden">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Package className="h-6 w-6 text-panashe-blue" />
              Luggage &amp; Cargo Manifest
            </h1>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Select value={selectedTripId} onValueChange={setSelectedTripId}>
              <SelectTrigger className="w-[280px]">
                <SelectValue placeholder="Select Trip" />
              </SelectTrigger>
              <SelectContent>
                {trips.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.route_name || `${t.origin} → ${t.destination}`} ({t.departure_datetime?.slice(0, 10)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedDestination || 'all'} onValueChange={value => setSelectedDestination(value === 'all' ? '' : value)}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="All Destinations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Destinations</SelectItem>
                {stations.map(station => (
                  <SelectItem key={station.id} value={station.id}>
                    {station.name || station.city || station.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button onClick={handlePrint} className="btn-panashe">
              <Printer className="h-4 w-4 mr-2" /> Print Manifest
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-panashe-blue" />
          </div>
        ) : !manifestData ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              <AlertCircle className="h-10 w-10 mx-auto mb-2 text-slate-400" />
              <p>Please select a trip to view luggage manifest.</p>
            </CardContent>
          </Card>
        ) : (
          <div ref={printRef} className="printable-manifest space-y-6">
            {/* Manifest Header Document */}
            <Card className="card-panashe border-2 border-slate-300">
              <CardContent className="p-6">
                <div className="flex items-start justify-between border-b pb-4">
                  <div className="flex items-center gap-3">
                    <img 
                      src="/panashe-logo.jpg" 
                      alt="Panashe Coaches" 
                      className="h-16 w-16 rounded object-cover border"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div>
                      <h2 className="font-heading text-2xl font-black tracking-wide text-slate-900">
                        PANASHE COACHES
                      </h2>
                      <p className="text-sm font-bold text-panashe-red tracking-wider uppercase">
                        Official Cross-Border Luggage &amp; Parcel Manifest
                      </p>
                      <p className="text-xs text-slate-500">Harare • Bulawayo • Masvingo • Beitbridge • Durban</p>
                    </div>
                  </div>

                  <div className="text-right text-xs">
                    <p><span className="text-slate-500">Printed:</span> <strong>{format(new Date(), 'dd/MM/yyyy HH:mm')}</strong></p>
                    <p><span className="text-slate-500">Trip Status:</span> <strong className="uppercase">{trip.status || 'N/A'}</strong></p>
                    <p><span className="text-slate-500">Total Items:</span> <strong className="text-blue-700 text-sm">{manifestData.total_items}</strong></p>
                  </div>
                </div>

                {/* Trip & Crew Meta Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 text-xs border-b bg-slate-50 rounded-lg px-4 my-4">
                  <div>
                    <span className="text-slate-500 block">Route:</span>
                    <strong className="text-slate-900 text-sm">{routeDisplay}</strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Departure:</span>
                    <strong className="text-slate-900 text-sm">
                      {departureValue ? format(parseISO(departureValue), 'dd MMM yyyy @ HH:mm') : 'N/A'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Driver:</span>
                    <strong className="text-slate-900">{trip.driver_name || 'Assigned Driver'}</strong>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Host:</span>
                    <strong className="text-slate-900">{trip.conductor_name || 'Assigned Host'}</strong>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="grid grid-cols-3 gap-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs font-semibold mb-4 text-blue-950">
                  <div>
                    <span className="text-blue-600 block">Total Luggage Charges:</span>
                    <span className="block text-base font-bold">USD ${Number(manifestData.total_charge_usd || 0).toFixed(2)}</span>
                    <span className="block text-sm">ZAR R{Number(manifestData.total_charge_zar || 0).toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-green-700 block">Total Collected / Paid:</span>
                    <span className="block text-base font-bold text-green-700">USD ${Number(manifestData.total_paid_usd || 0).toFixed(2)}</span>
                    <span className="block text-sm text-green-700">ZAR R{Number(manifestData.total_paid_zar || 0).toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-orange-700 block">Pay-Forward Balance Due:</span>
                    <span className="block text-base font-bold text-orange-700">USD ${Number(manifestData.total_balance_due_usd || 0).toFixed(2)}</span>
                    <span className="block text-sm text-orange-700">ZAR R{Number(manifestData.total_balance_due_zar || 0).toFixed(2)}</span>
                  </div>
                </div>

                {/* Route Parcel Stations Route strip */}
                {parcelPoints.length > 0 && (
                  <div className="mb-4 text-xs bg-purple-50 p-2.5 rounded border border-purple-200">
                    <span className="font-bold text-purple-900">📦 Authorized Route Parcel Stations: </span>
                    <span className="text-purple-800">{parcelPoints.join(' • ')}</span>
                  </div>
                )}

                {/* Luggage Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left border border-slate-200">
                    <thead className="bg-slate-800 text-white uppercase text-[11px]">
                      <tr>
                        <th className="p-2 border">Tag #</th>
                        <th className="p-2 border">Description</th>
                        <th className="p-2 border text-center">Qty</th>
                        <th className="p-2 border">Sender / Phone</th>
                        <th className="p-2 border">Receiver / Phone</th>
                        <th className="p-2 border">Pick-up Location</th>
                        <th className="p-2 border">Drop-off Location</th>
                        <th className="p-2 border text-right">Charge</th>
                        <th className="p-2 border text-right">Paid</th>
                        <th className="p-2 border text-right">Balance Due</th>
                        <th className="p-2 border text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {luggageList.length === 0 ? (
                        <tr>
                          <td colSpan={11} className="p-6 text-center text-slate-400">
                            No registered luggage items for this trip yet.
                          </td>
                        </tr>
                      ) : (
                        luggageList.map((item, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="p-2 border font-mono font-bold text-panashe-blue">
                              {item.luggage_ref}
                            </td>
                            <td className="p-2 border">
                              <span className="font-medium">{item.description}</span>
                              {item.is_fragile && (
                                <span className="ml-1 text-[10px] bg-red-100 text-red-700 px-1 py-0.5 rounded font-bold">FRAGILE</span>
                              )}
                            </td>
                            <td className="p-2 border text-center font-bold">{item.quantity}</td>
                            <td className="p-2 border">
                              <div>{item.sender_name}</div>
                              <div className="text-[10px] text-slate-500 font-mono">{item.sender_phone}</div>
                            </td>
                            <td className="p-2 border">
                              <div>{item.receiver_name || 'N/A'}</div>
                              <div className="text-[10px] text-slate-500 font-mono">{item.receiver_phone || ''}</div>
                            </td>
                            <td className="p-2 border font-medium text-emerald-700">
                              {item.pickup_point || item.origin || 'Route Start'}
                            </td>
                            <td className="p-2 border font-medium text-red-700">
                              {item.dropoff_point || item.custom_destination || item.destination || 'Final Destination'}
                            </td>
                            <td className="p-2 border text-right font-mono font-bold">
                              {item.currency} {Number(item.total_charge || 0).toFixed(2)}
                            </td>
                            <td className="p-2 border text-right font-mono text-green-700">
                              {item.currency} {Number(item.amount_paid || 0).toFixed(2)}
                            </td>
                            <td className="p-2 border text-right font-mono font-bold text-orange-700">
                              {item.is_pay_forward && item.pay_forward_balance > 0
                                ? `${item.currency} ${Number(item.pay_forward_balance).toFixed(2)}`
                                : '-'}
                            </td>
                            <td className="p-2 border text-center">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${
                                item.status === 'loaded' ? 'bg-green-100 text-green-800' :
                                item.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                                item.status === 'offloaded' ? 'bg-orange-100 text-orange-800' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Signature footer */}
                <div className="mt-8 pt-6 border-t grid grid-cols-3 gap-6 text-xs text-slate-600">
                  <div className="border-t border-slate-400 pt-2">
                    <p className="font-bold">Host Signature:</p>
                    <p className="text-[11px] text-slate-400 mt-6">Date: ________________________</p>
                  </div>
                  <div className="border-t border-slate-400 pt-2">
                    <p className="font-bold">Driver Signature:</p>
                    <p className="text-[11px] text-slate-400 mt-6">Date: ________________________</p>
                  </div>
                  <div className="border-t border-slate-400 pt-2">
                    <p className="font-bold">Station Manager Check &amp; Release:</p>
                    <p className="text-[11px] text-slate-400 mt-6">Date: ________________________</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <style>{`
              @media print {
                @page { size: A4 landscape; margin: 6mm; }
                nav, header, footer, .print\\:hidden { display: none !important; }
                body * { visibility: hidden; }
                .printable-manifest, .printable-manifest * { visibility: visible; }
                .printable-manifest {
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: 100% !important;
                  max-width: none !important;
                  margin: 0 !important;
                  overflow: visible !important;
                }
                .printable-manifest .overflow-x-auto { overflow: visible !important; }
                .printable-manifest .card-panashe { box-shadow: none !important; }
                .printable-manifest table { width: 100% !important; table-layout: fixed !important; border-collapse: collapse !important; }
                .printable-manifest th,
                .printable-manifest td { padding: 2px !important; font-size: 7px !important; line-height: 1.15 !important; white-space: normal !important; overflow-wrap: anywhere !important; vertical-align: top !important; }
                .printable-manifest th:nth-child(1), .printable-manifest td:nth-child(1) { width: 7%; }
                .printable-manifest th:nth-child(2), .printable-manifest td:nth-child(2) { width: 15%; }
                .printable-manifest th:nth-child(3), .printable-manifest td:nth-child(3) { width: 4%; }
                .printable-manifest th:nth-child(4), .printable-manifest td:nth-child(4) { width: 11%; }
                .printable-manifest th:nth-child(5), .printable-manifest td:nth-child(5) { width: 11%; }
                .printable-manifest th:nth-child(6), .printable-manifest td:nth-child(6) { width: 10%; }
                .printable-manifest th:nth-child(7), .printable-manifest td:nth-child(7) { width: 10%; }
                .printable-manifest th:nth-child(8), .printable-manifest td:nth-child(8) { width: 7%; }
                .printable-manifest th:nth-child(9), .printable-manifest td:nth-child(9) { width: 7%; }
                .printable-manifest th:nth-child(10), .printable-manifest td:nth-child(10) { width: 9%; }
                .printable-manifest th:nth-child(11), .printable-manifest td:nth-child(11) { width: 9%; }
                .printable-manifest tr { break-inside: avoid; page-break-inside: avoid; }
              }
            `}</style>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LuggageManifestPage;
